import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IRestaurantsContext, RestaurantsContext } from '../../../restaurantsState/restaurantsContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { UserRoles, IGetCustomersShort, TArgumentsForGetCustomersShort, EntityStatus } from 'types';
import { getCustomersShortInfo } from 'backend/customers';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import style from './CustomerSelect.module.scss';

const CustomerSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDCustomer, IDSelectedCustomer, IDSelectedCountry, IDSelectedCity } =
		useContext<IRestaurantsContext>(RestaurantsContext);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const { response, loading, refetch, responseError } = useFetchData<
		IGetCustomersShort,
		TArgumentsForGetCustomersShort
	>(hasAccess ? getCustomersShortInfo : null, { countryId: IDSelectedCountry, cityId: IDSelectedCity });
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	const value = !loading ? IDSelectedCustomer : undefined;

	const customers = useMemo(() => {
		const items = hasAccess ? response?.data.customers : loggedUser.customers || [];
		const visibleItems = showArchived
			? items
			: items?.filter(
					(customer) => customer.status === EntityStatus.Active || customer.id === IDSelectedCustomer,
			  );

		return visibleItems?.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser, showArchived, IDSelectedCustomer]);

	const handleSelect = (value: string) => {
		setIDCustomer(value);
	};

	useEffect(() => {
		if (isFirstRender) {
			setIsFirstRender(false);
		} else {
			refetch();
		}
	}, [IDSelectedCountry, IDSelectedCity]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<OutsideFormSelect
			containerClassName={style.container}
			className={style.select}
			testId="CustomerSelect"
			name="CustomerSelect"
			label="customerLabel"
			onChange={handleSelect}
			onRefetch={refetch}
			value={value}
			options={customers}
			listItemHeight={customers?.length}
			disabled={loading}
			loading={loading}
			fetchError={responseError}
			withValidation={false}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default CustomerSelect;
